import { z } from 'zod';
import { MoneySchema } from './common.js';
export const JEStatusSchema = z.enum([
    'DRAFT',
    'PENDING',
    'APPROVED',
    'POSTED',
    'REJECTED',
    'RETURNED',
    'REVERSED',
]);
// ============================================================
// JournalLine — child rows of a JournalEntry
// ============================================================
export const JournalLineSchema = z.object({
    id: z.uuid(),
    entryId: z.uuid(),
    lineNo: z.number().int().positive(),
    accountCode: z.string().min(1),
    // Prisma Decimal(18,2): accept canonical string form or a non-negative number.
    // Same convention as the rest of the codebase (see project.ts/loan.ts).
    debit: z.union([MoneySchema, z.coerce.number().nonnegative()]),
    credit: z.union([MoneySchema, z.coerce.number().nonnegative()]),
    description: z.string().nullable().optional(),
    refKey: z.string().nullable().optional(),
    // Accounting dimensions (Phase 42). Resolved per line; default down from the
    // header when the caller leaves them null.
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
});
// Wire shape for creating a single line — id/entryId/lineNo are server-assigned.
export const CreateJournalLineSchema = JournalLineSchema.omit({
    id: true,
    entryId: true,
    lineNo: true,
});
// ============================================================
// JournalEntry — header
// ============================================================
export const JournalEntrySchema = z.object({
    id: z.uuid(),
    voucherNo: z.string().min(1),
    voucherDate: z.coerce.date(),
    companyId: z.uuid(),
    // 'YYYYMM', e.g. '202605'
    period: z.string().regex(/^\d{6}$/),
    description: z.string().min(1),
    status: JEStatusSchema,
    postedAt: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    idempotencyKey: z.string().nullable().optional(),
    // Primary dimensions of the voucher (Phase 42).
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    // Workflow fields (Spec #3)
    createdBy: z.string().nullable().optional(),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    currentStep: z.number().int().nonnegative().optional(),
    // List-only computed totals (sum of line debits/credits). Optional so the
    // by-id shape and the 22 internal createJournalEntry callers are unaffected.
    totalDebit: z.union([MoneySchema, z.coerce.number().nonnegative()]).optional(),
    totalCredit: z.union([MoneySchema, z.coerce.number().nonnegative()]).optional(),
});
// Wire shape for POST — voucherNo is generated server-side. Dates are wire-as-ISO
// per Phase 3b convention (`z.iso.date()` for date-only fields; service converts
// to Date before Prisma).
export const CreateJournalEntrySchema = z.object({
    voucherDate: z.iso.date(),
    period: z.string().regex(/^\d{6}$/),
    description: z.string().min(1),
    // Optional document-type prefix override; defaults to 'JV'. Books are selected
    // by the service (first active book by bookCode asc) when not specified.
    prefix: z.string().min(1).max(8).default('JV'),
    // Primary dimensions of the voucher (Phase 42). Default down to every line
    // that doesn't carry its own branchId/departmentId.
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    // At least 2 lines (one debit + one credit) — the Dr=Cr balance check is
    // enforced in the service since it requires Decimal arithmetic.
    lines: z.array(CreateJournalLineSchema).min(2),
    // M7 Phase 1a (D) — sanctioned 4-eyes override reason for a manual JV that
    // genuinely needs to touch AP_TRADE (accrual / opening-balance / reclass).
    // Required ONLY when the draft touches AP_TRADE (enforced in the service guard).
    // A DIRECT immediate-POSTED path always strips this — a direct post is never
    // 4-eyes. Persisted to the append-only ApprovalEvent audit trail at draft time.
    overrideControlReason: z.string().min(1).optional(),
});
// ============================================================
// Approval-workflow schemas (Spec #3)
// ============================================================
export const JournalEntryApprovalSchema = z.object({
    id: z.uuid(),
    entryId: z.uuid(),
    stepNo: z.number().int().positive(),
    roleCode: z.string().min(1),
    roleLabel: z.string().min(1),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// M7 Phase 3 (SoD-by-actorId): optional audited justification that lets a
// holder of `approval.sodoverride` lift a self-approval block. Must be ACCEPTED
// here (plain optional) so nestjs-zod forwards it to the engine.
export const ApproveJournalEntrySchema = z.object({
    step: z.coerce.number().int().positive(),
    comment: z.string().optional(),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
// reject/return carry the step they act on, plus the reason
export const RejectJournalEntrySchema = z.object({
    step: z.coerce.number().int().positive(),
    reason: z.string().min(1),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
export const ReturnJournalEntrySchema = z.object({
    step: z.coerce.number().int().positive(),
    reason: z.string().min(1),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
// PATCH body for editing a DRAFT/RETURNED entry — same shape as create.
export const UpdateJournalEntrySchema = CreateJournalEntrySchema;
//# sourceMappingURL=journal-entry.js.map